import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
// PrimeNG 21 Imports
import { MessageService, MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ColumnConfig, TABLE_CONFIG } from '../dashboard/table-config';
import { ResponsibleRegistryService } from '../../services/responsible-registry.service';
import { Responsible } from '../../models/responsible.model';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ResponsibleDetailModalComponent } from './responsible-detail/responsible-detail.component';

@Component({
  selector: 'app-responsibles-registry',
  standalone: true,
  providers: [DialogService, MessageService],
  imports: [
    CommonModule,
    DynamicDialogModule,
    Toast,
    RegistryTableComponent,
    BreadcrumbModule,
  ],
  templateUrl: './responsibles-registry.component.html',
  styleUrl: './responsibles-registry.component.scss',
})
export class ResponsiblesRegistryComponent implements OnInit {
  private dialogService = inject(DialogService);
  private responsibleService = inject(ResponsibleRegistryService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private titleService = inject(Title);

  // Table configuration
  tableTitle = 'Responsabili Registrati';
  tableIcon = 'pi-users';
  columns: ColumnConfig[] = [];
  data = signal<any[]>([]);
  loading = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([{ label: 'Registro Responsabili' }]);

  ngOnInit(): void {
    this.titleService.setTitle('Registro Responsabili - PalEasy');
    this.loadConfiguration();
    this.loadManagers();
  }

  private loadConfiguration(): void {
    const config = TABLE_CONFIG['elencoResponsabili'];
    if (config) {
      this.columns = config.columns;
    }
  }

  private loadManagers(): void {
    this.loading.set(true);
    this.responsibleService.getResponsibles().subscribe({
      next: (response) => {
        // Usa metodi centralizzati del servizio
        const dataArray = this.responsibleService.extractResponsiblesArray(response);
        const mappedData = this.responsibleService.mapResponsiblesForDisplay(dataArray);
        console.log('Responsibles Registry - Mapped data:', mappedData);
        this.data.set(mappedData);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading managers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel recupero dei dati',
          detail: 'Impossibile caricare l\'elenco dei responsabili. Riprova più tardi.',
          life: 5000
        });
        this.data.set([]);
        this.loading.set(false);
      }
    });
  }

  // =====================
  // EVENT HANDLERS
  // =====================
  onDetailClick(rowData: any): void {
    if (rowData?.id) {
      this.router.navigate(['/axa/responsible', rowData.id]);
    }
  }

  onDeleteClick(rowData: any): void {
    if (rowData?.id) {
      if (confirm(`Sei sicuro di voler eliminare il responsabile "${rowData.businessName}"?`)) {
        this.responsibleService.deleteResponsible(rowData.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminazione completata',
              detail: 'Il responsabile è stato eliminato con successo',
              life: 3000
            });
            this.loadManagers();
          },
          error: (error) => {
            console.error('Error deleting responsible:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare il responsabile. Riprova più tardi.',
              life: 5000
            });
          }
        });
      }
    }
  }

  onExport(): void {
    this.responsibleService.exportResponsiblesRegistry().subscribe({
      next: (response: any) => {
        const blob = response.body as Blob | null;
        if (!blob) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore export',
            detail: 'File non ricevuto dal server',
            life: 4000
          });
          return;
        }
        let fileName = 'Registro Responsabili.xlsx';
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            fileName = decodeURIComponent(match[1]);
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore export',
          detail: 'Impossibile esportare il registro responsabili',
          life: 4000
        });
      }
    });
  }

  onClearFilters(): void {
    // Reset data to original
    this.loadManagers();
  }

  goToResponsibleWizard(): void {
    // TODO: Create manager registration wizard route
    this.messageService.add({
      severity: 'info',
      summary: 'Navigazione',
      detail: 'Wizard registrazione responsabile in sviluppo',
      life: 3000
    });
  }
}
